/**
 * Authentication page for signing up and logging in.
 * It provides an option for users to create a username during signup, which is
 * required for other apps where the same login information can be used.
 */



import CloseIcon from "@mui/icons-material/Close";
import LoadingButton from "@mui/lab/LoadingButton";
import { Button, Fab, FormControl } from "@mui/material";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  createUserWithEmailAndPassword,
  reload,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { get, getDatabase, ref, set } from "firebase/database";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, database } from "../firebase.js";

//to generate referal code for all users
const generateReferralCode = (userUID) => {
  return `${userUID.slice(0, 6)}-${Math.floor(Math.random() * 1000000)}`;
};

//to santitized username before saving
async function validateInput(text) {
  if (!text.trim()) {
    return false;
  }
  const regex = /^[a-zA-Z0-9_]*$/;
  if (regex.test(text)) {
    const sanitizedText = text.replace(/\s/g, "").toLowerCase();
    return sanitizedText;
  } else {
    return false;
  }
}

//function to check if the usrname exist
async function isUsernameAvailable(username) {
  const usernameRef = ref(database, `usernames/${username.toLowerCase()}`);
  const snapshot = await get(usernameRef);

  if (snapshot.exists()) {
    return false;
  }
  return true;
}

export function UserForm({ formDisplay }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [username, setUsername] = useState("");

  const navigate = useNavigate();

  function checkInput() {
    if (email === "" || password === "") {
      setMessage("Please fill in all fields");
      return false;
    } else {
      return true;
    }
  }

  const handleEmailchange = (event) => {
    setEmail(event.target.value);
  };
  const handlePasswordChamge = (event) => {
    setPassword(event.target.value);
  };

  const handleUsernameChamge = (event) => {
    setUsername(event.target.value);
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!checkInput() || username === "") {
      return;
    }
    setMessage("submiting...");
    setSubmitting(true);
    try {
      const sanitizedUser = await validateInput(username);
      if (!sanitizedUser) {
        alert(
          "Username can only contain alphabets, numbers, and underscores and be in lowercase"
        );
        return;
      }

      // Check if username is available
      const isAvailable = await isUsernameAvailable(sanitizedUser);
      if (!isAvailable) {
        alert("Username is taken");
        return;
      }

      // Create user with email and password
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const userId = userCredential.user.uid;

      // Save the username in the Realtime Database
      const db = getDatabase();
      const usernameRef = ref(db, `usernames/${sanitizedUser}`);

      const generatedReferralCode = generateReferralCode(userId);
      const userRef = ref(db, `users/${userId}`);

      await set(usernameRef, { userId });
      await set(userRef, {
        email: userCredential.user.email,
        username: sanitizedUser,
        referralCode: generatedReferralCode,
      });


      const user = auth.currentUser;
      if (user) {
        await reload(user); // Reload user to ensure the latest data
        await sendEmailVerification(user);
      }
      // Redirect user to the email verification page
      navigate("/emailverification");
      return;
    } catch (error) {
      switch (error.code) {
        case "auth/email-already-in-use":
          setMessage("This email is already in use.");
          break;
        case "auth/invalid-email":
          setMessage("Invalid email address.");
          break;
        case "auth/weak-password":
          setMessage("Password should be at least 6 characters.");
          break;
        default:
          setMessage(error.message || "Something went wrong.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!checkInput()) {
      return;
    }
    setMessage("submiting...");
    setSubmitting(true);
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;

      if (!user.emailVerified) {
        alert(
          "Email not verified",
          "Please verify your email before logging in."
        );

        navigate("/emailverification");
        await signOut(auth);
        return;
      }

      navigate("/authorized");
      //catch error for different usecase
    } catch (error) {
      switch (error.code) {
        case "auth/user-not-found":
          setMessage("No user found with this email.");
          break;
        case "auth/wrong-password":
          setMessage("Incorrect password.");
          break;
        case "auth/invalid-email":
          setMessage("Invalid email address.");
          break;
        default:
          setMessage(error.message || "An error occurred during sign-in.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      style={{
        padding: "2rem",
        margin: "2rem",
        boxShadow: "1px 1px 2px 1px ",
        justifyContent: "center",
        backgroundColor: "white",
        selfAlign: "start",
      }}
    >
      <Fab
        size="small"
        onClick={formDisplay}
        style={{ position: "absolute", right: 0, top: 0 }}
      >
        <CloseIcon />
      </Fab>
      <Typography
        style={{
          textAlign: "center",
          color: "gray",
        }}
        variant="h6"
        gutterBottom
      >
        signup/login
      </Typography>
      <Typography
        style={{
          textAlign: "center",
          color: "red",
        }}
        variant="h6"
        gutterBottom
      >
        {message}
      </Typography>
      <FormControl>
        <TextField
          onChange={handleEmailchange}
          value={email}
          name="email"
          required
          id="outlined-email-input"
          label="Email"
          type="email"
          variant="filled"
          style={{ margin: "0.5rem", marginTop: "1rem" }}
        />
        <TextField
          onChange={handlePasswordChamge}
          value={password}
          name="password"
          required
          id="outlined-password-input"
          label="Password"
          type="password"
          variant="filled"
          style={{ margin: "0.5rem" }}
        />

        <TextField
          onChange={handleUsernameChamge}
          value={username}
          name="username"
          required
          id="outlined-password-input"
          label="username for signup"
          type="text"
          variant="filled"
          style={{ margin: "0.5rem" }}
        />
        <Typography
          onClick={() => navigate("/forgotpassword")}
          style={{
            textAlign: "center",
            color: "red",
            cursor: "pointer",
          }}
          variant="h9"
          gutterBottom
        >
          forgot password?
        </Typography>
        {submitting ? (
          <LoadingButton
            style={{ margin: "0.5rem" }}
            loading
            variant="contained"
          >
            Submit
          </LoadingButton>
        ) : (
          " "
        )}

        {!submitting ? (
          <>
            <Button
              onClick={handleSignup}
              variant="outlined"
              size="large"
              style={{ margin: "0.5rem" }}
            >
              Signup
            </Button>
            <Button
              onClick={handleLogin}
              variant="outlined"
              size="large"
              style={{ margin: "0.5rem" }}
            >
              Login
            </Button>
          </>
        ) : (
          ""
        )}
      </FormControl>
    </Box>
  );
}
